/**
 * Projects Unified View
 *
 * Combines "New Project" (QuotationBuilder) and "Project History" (ProjectsTable)
 * into a single tabbed interface for streamlined workflow.
 *
 * @module pages/projects/ProjectsUnifiedView
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Container, Tabs, Tab, Row, Col, Badge } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import QuotationBuilder from '../quotations/QuotationBuilder';
import ProjectsTable from '../../components/projects/ProjectsTable';
import FilterBar from '../../components/projects/FilterBar';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { listProjects, getBudgetTotalsByProject } from '../../services/projectService';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';
import ProjectTypesContent from '../settings/ProjectTypesSettings';

// Los seis valores de `projects_status_check` en produccion. `draft` esta en
// el CHECK aunque `PROJECT_STATUS` todavia no lo exponga, asi que se lista
// aparte para no rechazar un ?status=draft legitimo.
const STATUSES_DEL_CHECK = [
  'draft',
  'quotation_sent',
  'quotation_accepted',
  'po_received',
  'in_progress',
  'closed',
];

/**
 * ProjectsUnifiedView Component
 *
 * Main container for the unified projects view with tabs:
 * - "Nuevo Proyecto": Embeds QuotationBuilder for creating new projects
 * - "Historial": Shows filterable table of all projects
 */
const ProjectsUnifiedView = () => {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Client filter arriving from "Ver Proyectos" quick action on ClientDetail
  // (?client=<id>, see src/pages/clients/ClientDetail.js). When present it
  // forces the Historial tab and pre-filters the listing by that client (#421).
  const initialClientParam = searchParams.get('client') || '';

  // Status filter arriving from the "Proyectos Activos" KPI of /dashboard
  // (?status=in_progress). Antes esta pantalla leia solo `client` y `tab`, asi
  // que el KPI decia "N activos" y el clic mostraba los 71 proyectos sin
  // filtrar (P1-E6). Se valida contra `projects_status_check`: un valor que la
  // base no admite se ignora, en vez de producir una lista vacia que miente.
  const initialStatusParam = STATUSES_DEL_CHECK.includes(searchParams.get('status'))
    ? searchParams.get('status')
    : '';

  // Get active tab from URL or default to 'historial'. A client filter always
  // forces the Historial tab, regardless of the ?tab= param.
  const initialTab = (initialClientParam || initialStatusParam)
    ? 'historial'
    : (searchParams.get('tab') || 'historial');
  const [activeTab, setActiveTab] = useState(initialTab);

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    status: initialStatusParam,
    client_id: initialClientParam,
    assigned_to: '',
    date_from: '',
    date_to: '',
    page: 0,
    pageSize: 20,
  });

  // Keep the client filter in sync with ?client= for param changes that
  // happen without a remount (e.g. browser back/forward while already on
  // /proyectos). On mount this is a no-op since the lazy state above already
  // picked it up.
  const clientParam = searchParams.get('client') || '';
  useEffect(() => {
    if (!clientParam) return;
    setActiveTab('historial');
    setFilters((prev) => (prev.client_id === clientParam ? prev : { ...prev, client_id: clientParam, page: 0 }));
  }, [clientParam]);

  // If the client filter is cleared (chip "x" or the FilterBar's "Limpiar
  // filtros"), drop the now-stale ?client= param so a reload doesn't
  // resurrect the filter.
  useEffect(() => {
    if (filters.client_id || !searchParams.get('client')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('client');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.client_id]);

  // Fetch projects with filters.
  //
  // `unwrapServiceResult` no es decorativo: `listProjects` cierra con
  // `catch { return { data:null, count:0, error } }`, o sea RESUELVE ante un
  // fallo. Sin desenvolver, el `error` de react-query es estructuralmente
  // siempre null, el bloque de error de ProjectsTable queda inalcanzable y una
  // caida de red se pinta como "No se encontraron proyectos" sobre los 73
  // proyectos reales de produccion. Ver utils/serviceResult.js.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['projects', filters],
    // NOTE: projectService.listProjects reads `clientId` (camelCase), while
    // the local filters state uses `client_id` to match FilterBar's existing
    // contract — map it here rather than duplicating filter state.
    queryFn: () => unwrapServiceResult(listProjects({ ...filters, clientId: filters.client_id || undefined })),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: activeTab === 'historial', // Only fetch when on historial tab
  });

  // Resolve the filtered client's display name for the chip. Shares the
  // 'clients-filter' query key with FilterBar so the two dedupe into a
  // single request instead of duplicating the fetch.
  const { data: filterClients } = useQuery({
    queryKey: ['clients-filter'],
    queryFn: async () => {
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('id, company_name, primary_contact_name')
        .order('company_name');
      if (clientsError) throw clientsError;
      return clientsData;
    },
    staleTime: 1000 * 60 * 10,
    enabled: !!filters.client_id,
  });

  const filterClientName = useMemo(() => {
    if (!filters.client_id) return null;
    const match = filterClients?.find((c) => c.id === filters.client_id);
    return match?.company_name || match?.primary_contact_name || null;
  }, [filterClients, filters.client_id]);

  const handleClearClientFilter = () => {
    setFilters((prev) => ({ ...prev, client_id: '', page: 0 }));
  };

  // Fetch budget (gastado/uso%) totals for just the projects on the current
  // page, in one batched call instead of one query per row (#406).
  const pageProjectIds = useMemo(
    () => (data?.data || []).map((p) => p.id),
    [data]
  );

  const { data: budgetTotals, isLoading: isBudgetLoading } = useQuery({
    queryKey: ['projects-budget-totals', pageProjectIds],
    queryFn: () => getBudgetTotalsByProject(pageProjectIds),
    staleTime: 1000 * 60 * 5,
    enabled: activeTab === 'historial' && pageProjectIds.length > 0,
  });

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      // Reset to first page when filters change (except when changing page)
      page: field === 'page' ? value : 0,
    }));
  };

  // Handle successful quotation/project creation
  const handleQuotationCreated = (result) => {
    // Log success for debugging
    if (result?.projectNumber) {
      console.log(`Project ${result.projectNumber} created successfully`);
    }

    // Invalidate and refetch projects
    queryClient.invalidateQueries(['projects']);

    // Switch to historial tab
    setActiveTab('historial');
    setSearchParams({ tab: 'historial' });

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle tab change
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey });
  };

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Page Header */}
        <Row className="mb-4">
        <Col>
          <h2 className="mb-1">
            <i className="bi bi-folder me-2"></i>
            Proyectos
          </h2>
          <p className="text-muted mb-0">
            Gestiona proyectos y cotizaciones en un solo lugar
          </p>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onSelect={handleTabChange}
        className="mb-4"
        id="projects-tabs"
      >
        {/* Tab 1: Nuevo Proyecto */}
        <Tab
          eventKey="nuevo"
          title={
            <>
              <i className="bi bi-plus-circle me-2"></i>
              Nuevo Proyecto
            </>
          }
        >
          <div className="pt-3">
            <QuotationBuilder
              embedded={true}
              mode="create_project"
              onSuccess={handleQuotationCreated}
            />
          </div>
        </Tab>

        {/* Tab 2: Historial de Proyectos */}
        <Tab
          eventKey="historial"
          title={
            <>
              <i className="bi bi-clock-history me-2"></i>
              Historial de Proyectos
            </>
          }
        >
          <div className="pt-3">
            {/* Active client filter chip (arrived via ?client=, or picked in FilterBar) */}
            {filters.client_id && (
              <div className="mb-3" data-testid="client-filter-chip">
                <Badge
                  bg="primary"
                  className="d-inline-flex align-items-center gap-2 px-3 py-2 fw-normal"
                >
                  <span>
                    Cliente: {filterClientName || 'Cargando...'}
                  </span>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    style={{ fontSize: '0.65rem' }}
                    aria-label="Quitar filtro de cliente"
                    onClick={handleClearClientFilter}
                  ></button>
                </Badge>
              </div>
            )}

            {/* Filter Bar */}
            <FilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              projects={data?.data || []}
            />

            {/* Projects Table */}
            <ProjectsTable
              projects={data?.data || []}
              isLoading={isLoading}
              error={error}
              onRetry={refetch}
              filters={filters}
              onFilterChange={handleFilterChange}
              totalCount={data?.count || 0}
              budgetTotals={budgetTotals || {}}
              isBudgetLoading={isBudgetLoading}
            />
          </div>
        </Tab>

        {/* Tab 3: Tipos de Proyecto (admin only) */}
        {isAdmin && (
          <Tab
            eventKey="tipos"
            title={
              <>
                <i className="bi bi-tags me-2"></i>
                Tipos de Proyecto
              </>
            }
          >
            <div className="pt-3">
              <ProjectTypesContent />
            </div>
          </Tab>
        )}
      </Tabs>
      </Container>
    </DashboardLayout>
  );
};

export default ProjectsUnifiedView;
